import React, { useEffect } from "react";
import dynamic from "next/dynamic";
import { useSearchParams } from "next/navigation";

import getRegionComplianceRules from "@/api/requests/consumerApi/getRegionComplianceRules";
import { getDispensary } from "@/api/requests/getDispensary";
import getDispensaryReviews from "@/api/requests/getDispensaryReviews";
import { DISPENSARY_PAGES } from "@/constants/dispensary";
import { Dispensary } from "@/custom-types/Dispensary";
import { DispensaryReview } from "@/custom-types/Reviews";
import useFilters from "@/hooks/useFilters";
import { retailerReviewsMetaData } from "@/lib/retailerMetadata";
import { useDispatch } from "@/redux/hooks";
import {
  ComplianceRuleset,
  loadComplianceRuleset,
} from "@/redux/slices/complianceRules";
import { createGetServerSideProps } from "@/utils/createGetServerSideProps";
import generateDispensaryRedirect from "@/utils/generateDispensaryRedirect";
import { getFirstQueryParam } from "@/utils/nextRouterUtils";
import { throwOnRedirectHint } from "@/utils/redirectHints";
import { getCountryCodeHeader } from "@/utils/requestContext";

import DispensaryBreadcrumbs from "@/components/Dispensary/DispensaryBreadcrumbs";
import DispensaryHeader from "@/components/Dispensary/DispensaryHeader";
import DispensaryTabs from "@/components/Dispensary/DispensaryTabs";
import NoReviews from "@/components/Dispensary/Reviews/components/NoReviews";
import ReviewContainer, {
  ReviewType,
} from "@/components/Dispensary/Reviews/components/ReviewContainer";
import ReviewsHeader from "@/components/Dispensary/ReviewsHeader";
import MetaTags from "@/components/MetaTags";
import PageNavigationV2 from "@/components/PageNavigation";
import ResultCounts from "@/components/ResultCounts";
const Dropdown = dynamic(() => import("@/components/botanic/Dropdown"), {
  ssr: true,
});

const DISPENSARY_REVIEWS_PER_PAGE = 24;

/* eslint-disable sort-keys-fix/sort-keys-fix -- exception: order important to display */
const sortOptions: { [key: string]: string } = {
  Recommended: "rank_score:desc",
  "Most Helpful": "upvotes_count:desc",
  "Least Helpful": "upvotes_count:asc",
  Newest: "created:desc",
  Oldest: "created:asc",
};
/* eslint-enable sort-keys-fix/sort-keys-fix -- exception: order important to display */

type ReviewsPageProps = {
  complianceRuleset: ComplianceRuleset;
  dispensary: Dispensary;
  ratingsWithoutContent: number;
  reviews: DispensaryReview[];
  totalReviews: number;
};

const ReviewsPage: React.FC<ReviewsPageProps> = ({
  complianceRuleset,
  dispensary,
  ratingsWithoutContent,
  reviews,
  totalReviews,
}) => {
  const dispatch = useDispatch();
  const { swapFilter } = useFilters({ shallowRouting: false });
  const sortParam =
    useSearchParams()?.get("sort")?.toString() || sortOptions["Most Helpful"];
  const pageParam = Number(useSearchParams()?.get("page")) || 1;

  const { coverPhotoUrl, logoUrl, name } = dispensary;

  useEffect(() => {
    if (complianceRuleset) {
      dispatch(loadComplianceRuleset(complianceRuleset));
    }
  }, []);

  const page = Math.max(pageParam, 1);

  const hasReviews = totalReviews > 0;

  const { description, image, title } = retailerReviewsMetaData(
    coverPhotoUrl,
    logoUrl,
    name,
    page,
  );

  const offset = (page - 1) * DISPENSARY_REVIEWS_PER_PAGE;
  const startIndex = offset + 1;
  const rawEnd = page * DISPENSARY_REVIEWS_PER_PAGE;
  const endIndex = Math.min(rawEnd, totalReviews);

  return (
    <>
      <MetaTags
        canonicalParams={["page"]}
        description={description}
        image={image}
        robots={!hasReviews ? "noindex,noarchive" : undefined}
        shouldShowAlternates={false}
        title={title}
      />
      <DispensaryHeader dispensary={dispensary} />
      <DispensaryTabs
        activeTab={DISPENSARY_PAGES.reviews}
        dispensary={dispensary}
      />
      {hasReviews ? (
        <>
          <ReviewsHeader
            dispensary={dispensary}
            ratingsWithoutContent={ratingsWithoutContent}
            totalReviews={totalReviews}
            className="container mt-xl"
          />
          <section className="container mb-section mt-xl">
            {reviews.length && (
              <div className="flex justify-between">
                <ResultCounts
                  endIndex={endIndex}
                  label="Reviews"
                  startIndex={startIndex}
                  totalResults={totalReviews}
                />
                <Dropdown
                  data-testid="dispreview-sort"
                  label="Sort by"
                  variant="small"
                  className="mb-sm"
                  options={Object.keys(sortOptions)}
                  onItemRender={(option) => ({
                    children: option,
                    onClick: () => {
                      swapFilter([sortOptions[sortParam]], {
                        sort: sortOptions[option],
                      });
                    },
                  })}
                />
              </div>
            )}
            {reviews.map((review) => (
              <ReviewContainer
                key={review.id}
                retailType={dispensary.retailType}
                review={review}
                type={ReviewType.DISPENSARY}
                itemReviewed={dispensary.name}
                dispensaryAddress={{
                  address1: dispensary.address1,
                  city: dispensary.city,
                  country: dispensary.country,
                  state: dispensary.state,
                  zip: dispensary.zip,
                }}
              />
            ))}
            <PageNavigationV2
              currentPage={page}
              showRelativeLinkTags
              totalPages={Math.ceil(totalReviews / DISPENSARY_REVIEWS_PER_PAGE)}
            />
            <DispensaryBreadcrumbs dispensary={dispensary} />
          </section>
        </>
      ) : (
        <NoReviews dispensary={dispensary} />
      )}
    </>
  );
};

export const getServerSideProps = createGetServerSideProps<ReviewsPageProps>(
  async (context) => {
    const { query, req, resolvedUrl } = context;
    const { slug } = query;

    const dispensary = await throwOnRedirectHint(
      () => getDispensary(slug as string),
      slug as string,
    );

    const domainCountryCode = getCountryCodeHeader(req.headers);

    const redirect = generateDispensaryRedirect(
      domainCountryCode,
      resolvedUrl,
      query,
      dispensary,
    );

    if (redirect) {
      return { redirect };
    }

    // fallback if redirect not generated
    if (!dispensary) {
      return {
        notFound: true,
      };
    }

    const page = Math.max(Number(getFirstQueryParam(query.page, "1")), 1);
    const sort = getFirstQueryParam(query.sort, sortOptions["Recommended"]);

    const [
      { ratingsWithoutContent, reviews, totalReviews },
      complianceRuleset,
    ] = await Promise.all([
      getDispensaryReviews(
        slug as string,
        (page - 1) * DISPENSARY_REVIEWS_PER_PAGE,
        DISPENSARY_REVIEWS_PER_PAGE,
        encodeURI(sort),
      ),
      getRegionComplianceRules(dispensary.state, dispensary.country),
    ]);

    const pageDoesNotExist =
      page > 1 && totalReviews / DISPENSARY_REVIEWS_PER_PAGE < page - 1;

    if (pageDoesNotExist) {
      return {
        notFound: true,
      };
    }

    return {
      props: {
        complianceRuleset,
        dispensary,
        ratingsWithoutContent,
        reviews,
        totalReviews,
      },
    };
  },
);

export default ReviewsPage;
